/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/**
 * Schemas for the account API responses. See `README.md`.
 *
 * No `z.ZodType<Model>` annotations here: account-web compiles this file with
 * `"strict": false`, where zod infers every property as optional and any such
 * annotation fails. The drift guard lives in the types test instead — keep it
 * in step when editing a schema.
 */

import { z } from 'zod';
import type { AccountResponseSchemaKey } from './responseSchemaKeys';

const optionalString = z.string().optional();
const optionalBoolean = z.boolean().optional();
const optionalNumber = z.number().optional();
/** Timestamps arrive as ISO strings from some SSO routes and epoch millis from others. */
const optionalTimestamp = z.union([z.string(), z.number()]).optional();

/* ------------------------------ user profile ------------------------------ */

const FaceProfileSchema = z.object({
  faceId: optionalString,
  faceStatus: optionalNumber,
  timestamp: optionalString,
});

export const UserSchema = z.object({
  username: optionalString,
  gender: optionalString,
  notificationPreference: optionalBoolean,
  isInsured: optionalBoolean,
  cultureCode: optionalString,
  dialingCode: optionalString,
  mobilePhone: optionalString,
  status: optionalString,
  lastName: optionalString,
  phoneNumberVerified: optionalBoolean,
  updateNamesCount: optionalNumber,
  phoneNumber: optionalString,
  paxType: optionalString,
  firstName: optionalString,
  title: optionalString,
  loyaltyTier: optionalString,
  id: optionalString,
  loyaltyId: optionalString,
  navitaireRegistrationCompletedAt: optionalString,
  dateOfBirth: optionalString,
  city: optionalString,
  country: optionalString,
  countryCode: optionalString,
  state: optionalString,
  clientId: optionalString,
  navitaireCustomerNumber: optionalString,
  addressLine1: optionalString,
  addressLine2: optionalString,
  addressLine3: optionalString,
  nationality: optionalString,
  postalCode: optionalString,
  emergencyRelationship: optionalString,
  emergencyPhoneNumber: optionalString,
  emergencyFirstName: optionalString,
  emergencyLastName: optionalString,
  emergencyDialingCode: optionalString,
  bigMemberId: optionalString,
  parentGuardian: optionalString,
  parentGuardianFirstName: optionalString,
  parentGuardianLastName: optionalString,
  parentGuardianFullName: optionalString,
  parentGuardianEmail: optionalString,
  parentGuardianDocNumber: optionalString,
  parentGuardianRelationship: optionalString,
  relationship: optionalString,
  ipAddress: optionalString,
  walletId: optionalString,
  activatedAt: optionalString,
  createdAt: optionalString,
  createdBySocial: optionalString,
  firstLastNameChangeAt: optionalString,
  lastUpdatePwdDate: optionalString,
  updatedBy: optionalString,
  updateUsernameCount: optionalString,
  baggageTypeId: optionalString,
  email: optionalString,
  cityPreference: optionalString,
  newsletterIds: z.array(z.string()).optional(),
  otp: optionalString,
  faceId: optionalString,
  faceProfile: FaceProfileSchema.optional(),
  bigpayId: optionalString,
  bigpayLinkExpiresAt: optionalString,
  twoFactorAuthPreferences: z
    .object({ sms: z.boolean(), email: z.boolean() })
    .optional(),
});

export const CreditBalanceSchema = z.object({
  availableCredits: z.number().nullable(),
  currencyCode: z.string().nullable(),
  bigMemberId: z.string().nullable().optional(),
});

export const MergeEligibilitySchema = z.object({ isEligible: z.boolean() });

export const AccountDeletionSchema = z.object({
  deleteScheduledAt: optionalString,
});

export const BigMemberSearchSchema = z.object({
  username: optionalString,
  loyaltyId: optionalString,
  loyaltyTier: optionalString,
  navitaireCustomerNumber: optionalString,
  encrypted: optionalString,
});

/* ------------------------------ travel docs ------------------------------- */

const NationalIdDataSchema = z.object({
  issueCountry: optionalString,
  expiryDate: optionalString,
});

export const TravelDocSchema = z.object({
  expiryDate: optionalString,
  passportNumber: optionalString,
  mRZCode: optionalString,
  passportIssueCountry: optionalString,
  passportType: optionalString,
  icNumber: optionalString,
  ic: NationalIdDataSchema.optional(),
});

/* --------------------------- security activity ---------------------------- */

const SecurityActivityItemSchema = z
  .object({
    id: optionalString,
    userChangeRefId: optionalString,
    action: z.string().nullable().optional(),
    type: optionalString,
    activityType: optionalString,
    createdAt: optionalString,
    timestamp: optionalTimestamp,
  })
  .passthrough();

export const SecurityActivitySchema = z.object({
  items: z.array(SecurityActivityItemSchema).optional(),
});

export const RecentSecurityActivityDetailSchema = z
  .object({
    id: optionalString,
    device: optionalString,
    devicePlatform: optionalString,
    deviceType: optionalString,
    clientApp: optionalString,
    location: z
      .union([
        z.string(),
        z.object({
          city: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
        }),
      ])
      .nullable()
      .optional(),
    accessTime: optionalTimestamp,
    createdAt: optionalTimestamp,
    lastUsedAt: optionalTimestamp,
    type: optionalString,
  })
  .passthrough();

export const RegisteredPasskeysSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    deviceId: optionalString,
    icon: optionalString,
    iconUrl: optionalString,
    createdAt: optionalNumber,
    aaguid: optionalString,
    credentialDeviceType: optionalString,
    updatedAt: optionalNumber,
    authCounter: optionalNumber,
    lastUsedAt: optionalNumber,
    isThisDevice: optionalBoolean,
  }),
);

/* ------------------------- notification preferences ----------------------- */

const UnsubscribedListSchema = z
  .object({ unsubscribed: z.array(z.string()).optional() })
  .passthrough();

const NotificationChannelPreferenceSchema = z
  .object({
    isActive: optionalBoolean,
    categories: z.record(z.unknown()).optional(),
    subCategories: UnsubscribedListSchema.optional(),
    mainCategories: UnsubscribedListSchema.optional(),
  })
  .passthrough();

const NotificationChannelsSchema = z.record(
  NotificationChannelPreferenceSchema.optional(),
);

export const NotificationPreferencesSchema = z.object({
  notificationPreference: optionalBoolean,
  channels: NotificationChannelsSchema.optional(),
});

const NotificationPreferencesConfigChannelsSchema = z.object({
  sms: z.boolean(),
  email: z.boolean(),
  push: z.boolean(),
});

export const NotificationPreferencesConfigSchema = z.array(
  z.object({
    mainCategory: z.object({
      titleCode: z.string(),
      shortCode: z.string(),
      subtitleCode: z.string(),
    }),
    subCategories: z.array(
      z.object({
        titleCode: z.string(),
        subtitleCode: z.string(),
        internalCode: z.string(),
        channels: NotificationPreferencesConfigChannelsSchema,
        applicableCountries: z.array(z.string()),
      }),
    ),
  }),
);

/* --------------------- favourite members / addresses ---------------------- */

const FavouriteMemberSchema = z.object({
  dateOfBirth: optionalString,
  docNo: optionalString,
  expirationDate: optionalString,
  firstName: optionalString,
  gender: optionalString,
  id: optionalString,
  issuingCountry: optionalString,
  lastName: optionalString,
  loyaltyId: optionalString,
  nationality: optionalString,
  navitaireCustomerNumber: optionalString,
  title: optionalString,
  seq: optionalNumber,
  countryCode: optionalString,
  username: optionalString,
});

export const FavouriteMembersSchema = z.object({
  favoriteMembers: z.array(FavouriteMemberSchema).optional(),
});

export const ShippingAddressesSchema = z.array(
  z.object({
    area: optionalString,
    label: optionalString,
    contactName: optionalString,
    contactNumber: optionalString,
    isPreferred: optionalBoolean,
    country: optionalString,
    countryCode: optionalString,
    state: optionalString,
    shippingAddressId: optionalString,
    postalCode: optionalString,
    place: optionalString,
    dialingCode: optionalString,
    phoneNumber: optionalString,
  }),
);

/* ----------------------------- configuration ------------------------------ */

export const CountrySettingsSchema = z.object({ mobileLogin: z.boolean() });

export const UserDetailChangeConfigurationSchema = z.object({
  isChangePhoneNumberEnabled: optionalBoolean,
  isChangeUsernameEnabled: optionalBoolean,
  maxFirstNameChangeLength: optionalNumber,
  maxLastNameChangeLength: optionalNumber,
  maxTotalNameChangeLength: optionalNumber,
  userNamesUpdateLimit: optionalNumber,
  emailUpdateLimit: optionalNumber,
  mobilePhoneUpdateLimit: optionalNumber,
  isNameChangeLimitEnabled: optionalBoolean,
  isChangeUsernameViaEmailOtp: optionalBoolean,
});

const ConfigsSchema = z.object({
  isEnforcedPostLogin: optionalBoolean,
  isGeneralOtpLimitEnabled: optionalBoolean,
  generalOtpLimit: optionalNumber,
  isEnabledForPublic: optionalBoolean,
  isEnabledForUser: optionalBoolean,
});

export const OtpConfigurationSchema = z.object({
  isEnforcedPostLogin: optionalBoolean,
  isGeneralOtpLimitEnabled: optionalBoolean,
  generalOtpLimit: optionalNumber,
  isEnabledForPublic: optionalBoolean,
  isEnabledForUser: optionalBoolean,
  phoneVerification: ConfigsSchema.optional(),
  smsServiceProvider: ConfigsSchema,
});

/* ------------------------------ account menu ------------------------------ */

const AccountMenuSectionSchema = z.object({
  title: optionalString,
  section_title: optionalString,
  type: optionalString,
});

const AccountMenuChildItemSchema = z.object({
  id: z.string(),
  uuid: z.string(),
  title: z.string(),
  redirection_url: z.string(),
});

/**
 * `AccountMenuSubItem` nests itself, so `z.lazy` needs an explicit annotation —
 * TypeScript cannot infer a recursive type. `z.ZodTypeAny` rather than
 * `z.ZodType<AccountMenuSubItem>` for the `strict: false` reason in the file
 * header; runtime validation is unaffected.
 */
const AccountMenuSubItemSchema: z.ZodTypeAny = z.lazy(() =>
  z.object({
    id: z.string(),
    uuid: z.string(),
    title: z.string(),
    subtitle: optionalString,
    iconToken: optionalString,
    iconURL: optionalString,
    redirectionUrl: optionalString,
    section: AccountMenuSectionSchema.optional(),
    childItems: z.array(AccountMenuChildItemSchema).optional(),
    subMenuItems: z.array(AccountMenuSubItemSchema).optional(),
  }),
);

const AccountMenuItemSchema = z.object({
  id: z.string(),
  uuid: z.string(),
  title: z.string(),
  iconToken: optionalString,
  iconURL: optionalString,
  redirection_url: z.string(),
  subMenuItems: z.array(AccountMenuSubItemSchema),
});

export const AccountMenuContentSchema = z.object({
  success: optionalBoolean,
  data: z
    .object({ accountMenu: z.array(AccountMenuItemSchema).optional() })
    .optional(),
  metadata: z
    .object({
      timestamp: optionalString,
      version: optionalString,
      consumer: z
        .object({ lob: optionalString, feature: optionalString })
        .optional(),
    })
    .optional(),
});

/* ------------------------------ saved cards ------------------------------- */

const SavedCardItemSchema = z.object({
  paymentMethodId: z.string().nullable(),
  ccPaymentMethodId: z.string(),
  cardType: z.string(),
  creditCard: z.string(),
  expiryDate: z.string(),
  lastUpdateTMS: z.string(),
  urlBP: z.string(),
  urlCC: z.string(),
  virtualNo: z.string(),
  preferredCard: z.boolean(),
  eciStatus: z.string().nullable(),
  lastValidateTMS: z.string().nullable(),
  accountHolder: z.string(),
  countryCode: z.string().nullable(),
});

export const SavedCardsSchema = z.object({
  savedCardPanList: z.object({
    saveCards: z.array(SavedCardItemSchema),
    bigPay: z.array(SavedCardItemSchema),
    enableSaving: z.boolean(),
  }),
  errorCode: z.string().nullable(),
  errorMessage: z.string(),
});

/* ----------------------------- credit history ----------------------------- */

export const CreditHistorySchema = z.object({
  StatusCode: z.number(),
  Message: z.string(),
  Description: z.string(),
  Data: z
    .object({
      totalCredits: z.string(),
      collections: z.array(
        z.object({
          createdDate: z.string(),
          PNR: z.string(),
          expiryDate: z.string(),
          amount: z.string(),
          accountCollectionKey: z.string(),
          accountTransactionCode: z.string(),
        }),
      ),
    })
    .nullable(),
});

/* ---------------------------- favourited items ---------------------------- */

export const FavouritedItemsSchema = z.object({
  favouritedItems: z
    .array(
      z.object({
        favouritedItemId: z.string(),
        userId: z.string(),
        lineOfBusinessItemId: z.string(),
        lineOfBusiness: z.string(),
        webLink: optionalString,
        mobileLink: optionalString,
        data: z
          .object({
            name: optionalString,
            location: optionalString,
            guestRating: optionalString,
            image: optionalString,
          })
          .optional(),
        createdAt: z.number(),
      }),
    )
    .optional(),
});

/* ------------------------------ life profile ------------------------------ */

const RewardMoreActionItemSchema = z.object({
  buttonTitle: optionalString,
  buttonID: optionalString,
  iconURL: optionalString,
  deeplink: optionalString,
});

const RewardActionSchema = z.object({
  buttonTitle: optionalString,
  buttonID: optionalString,
  iconURL: optionalString,
  deeplink: optionalString,
  actions: z
    .object({
      actionTitle: optionalString,
      actionImageUrl: optionalString,
      actionItems: z.array(RewardMoreActionItemSchema).optional(),
    })
    .optional(),
});

const RewardsSchema = z.object({
  rewardsId: optionalString,
  headerTitle: optionalString,
  mainTitle: optionalString,
  cardTitle: optionalString,
  cardTitleColor: optionalString,
  cardIconURL: optionalString,
  actionTitle: optionalString,
  actionURL: optionalString,
  actionDeeplink: optionalString,
  actionTitleColor: optionalString,
  actionBgColor: optionalString,
  pointsStaticTitle: optionalString,
  pointsDeeplink: optionalString,
  redirectionIconURL: optionalString,
  points: optionalNumber,
  pointsTextColor: optionalString,
  bgImgURL: optionalString,
  actions: z.array(RewardActionSchema).optional(),
});

const SubscriptionItemSchema = z.object({
  id: optionalString,
  title: optionalString,
  titleColor: optionalString,
  description: optionalString,
  descriptionColor: optionalString,
  actionTitle: optionalString,
  actionTitleColor: optionalString,
  actionArrowUrl: optionalString,
  bgImgURL: optionalString,
  actionDeeplink: optionalString,
});

export const LifeProfileSchema = z.object({
  rewards: RewardsSchema.optional(),
  subscriptions: z
    .object({
      header: z.object({ title: optionalString }).optional(),
      data: z.array(SubscriptionItemSchema).optional(),
    })
    .optional(),
  shortcuts: z
    .array(
      z.object({
        id: optionalString,
        title: optionalString,
        titleColor: optionalString,
        iconURL: optionalString,
        actionDeeplink: optionalString,
        bgColor: optionalString,
      }),
    )
    .optional(),
  ssoUserId: optionalString,
  firstName: optionalString,
  lastName: optionalString,
  mobilePhone: optionalString,
  dialingCode: optionalString,
  email: optionalString,
  bigPoint: optionalNumber,
  dateOfBirth: optionalString,
  showBirthDate: optionalBoolean,
  showFullName: optionalBoolean,
  showEmail: optionalBoolean,
  nickname: optionalString,
  avatar: optionalString,
  biography: optionalString,
  membershipTier: optionalString,
  backgroundImage: optionalString,
  countriesVisited: z.array(z.string()).optional(),
  interests: z
    .array(
      z.object({
        id: optionalString,
        value: optionalString,
        tags: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  recommendedPosts: z
    .object({
      data: z.array(z.unknown()).optional(),
      message: optionalString,
      total: optionalNumber,
      pageNum: optionalNumber,
      pageSize: optionalNumber,
    })
    .optional(),
  isSuperPlusUser: optionalBoolean,
  url: optionalString,
  showPhoneNumber: optionalBoolean,
  socialJobPosition: optionalString,
  socialCompanyName: optionalString,
  socialFaceBook: optionalString,
  socialLinkedIn: optionalString,
  socialInstagram: optionalString,
  socialTwitter: optionalString,
});

/* ------------------------------ payment config ----------------------------- */

/** Carries the RSA public key for card encryption — a drift breaks every card save. */
export const PaymentConfigSchema = z.object({
  paymentConfigs: z.array(
    z.object({
      featureName: z.string(),
      value: z.string(),
    }),
  ),
});

/* -------------------------------- add card -------------------------------- */

export const AddCardSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: z
    .object({
      type: z.string(),
      redirectUrl: optionalString,
      requestMethod: optionalString,
      attributes: z
        .object({
          paRes: optionalString,
          paReq: optionalString,
          PaReq: optionalString,
          metaData: optionalString,
          MD: optionalString,
          termUrl: optionalString,
          TermUrl: optionalString,
        })
        .optional(),
      gatewayId: optionalString,
      errorCode: z.number(),
      cardSubmitted: z.boolean(),
    })
    .nullable(),
});

export const CardStatusSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: z.string().nullable(),
});

/* ------------------------------ bin settings ------------------------------ */

export const BinSettingsSchema = z.object({
  BinSettings: z
    .array(
      z.object({
        AllowCardBins: z.array(z.string()),
        BinRange: z.string(),
        BinRanges: z.array(
          z.object({
            EndRange: z.string().nullable(),
            PMID: z.number(),
            StartRange: z.string().nullable(),
          }),
        ),
        BlockCardBin: z.string(),
        CardBin: z.string(),
        CardImageSrcUrl: optionalString,
        PMID: z.string(),
        PayMethodCode: z.string(),
        PayOptMethodCode: z.string(),
      }),
    )
    .nullable(),
});

/* -------------------------------- registry -------------------------------- */

/**
 * Typed as a complete record, so adding a key to
 * `ACCOUNT_RESPONSE_SCHEMA_KEYS` without a schema fails to compile.
 */
export const accountResponseSchemas: Record<
  AccountResponseSchemaKey,
  z.ZodTypeAny
> = {
  user: UserSchema,
  creditBalance: CreditBalanceSchema,
  securityActivity: SecurityActivitySchema,
  recentSecurityActivityDetail: RecentSecurityActivityDetailSchema,
  notificationPreferences: NotificationPreferencesSchema,
  favouriteMembers: FavouriteMembersSchema,
  shippingAddresses: ShippingAddressesSchema,
  travelDoc: TravelDocSchema,
  registeredPasskeys: RegisteredPasskeysSchema,
  bigMemberSearch: BigMemberSearchSchema,
  mergeEligibility: MergeEligibilitySchema,
  accountDeletion: AccountDeletionSchema,
  countrySettings: CountrySettingsSchema,
  userDetailChangeConfiguration: UserDetailChangeConfigurationSchema,
  otpConfiguration: OtpConfigurationSchema,
  notificationPreferencesConfig: NotificationPreferencesConfigSchema,
  accountMenuContent: AccountMenuContentSchema,
  savedCards: SavedCardsSchema,
  paymentConfig: PaymentConfigSchema,
  addCard: AddCardSchema,
  cardStatus: CardStatusSchema,
  binSettings: BinSettingsSchema,
  creditHistory: CreditHistorySchema,
  favouritedItems: FavouritedItemsSchema,
  lifeProfile: LifeProfileSchema,
};
